import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Form, notification, Row, Col, Select } from 'antd';
import { observe, toJS } from 'mobx';
import moment from 'moment-timezone';
import _ from 'lodash';
import styled from 'styled-components';

import { AppStore, ShopStore, UserStore, CurrentUserStore } from 'stores';

import { StyledPitstopModal } from './common/styled';
import SelectPriorities from './common/SelectPriorities';
import FormMultiSelect from './common/FormMultiSelect';
import { WEEK_DAYS } from './AddEmailReportModal';
import TimePickerDropdown from './common/TimePickerDropdown';

const StyledP = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
`;

const UNSUBSCRIBE_ALERTS_MODAL_ID = 'UNSUBSCRIBE_ALERTS_MODAL_ID';

const UnsibscribeAlertsModal = observer(({ id, notifications, onSuccess }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (notifications?.priorities) {
      form.setFieldsValue({
        priorities: notifications.priorities,
      });
    }
    if (notifications?.diagnostics?.[0]) {
      const m = moment(notifications.diagnostics[0], 'MMM D, YYYY h:mm A');
      const day = m.format('e');
      const time = m.format('h:mmA').toLowerCase();

      form.setFieldsValue({
        days: Number(day),
        time,
      });
    }
  }, [notifications, form]);

  const onOkHandler = async () => {
    try {
      const values = await form.validateFields();

      const { days, time, priorities } = values;

      const shopId = ShopStore.currentShop.id;

      let timezone = CurrentUserStore.user.settings.timezone;
      if (!timezone) {
        timezone = 'America/Toronto';
      }

      const payload = {
        timezone,
        shopId,
        userId: notifications.userId,
      };

      const priorityNumberList =
        ShopStore.generatePriorityNumbersFromPriorityList(priorities);

      payload.priorities = priorityNumberList;

      if (days !== null && days !== undefined) {
        const sendNextEmailAt = getNextMailingDate(days, time, timezone);

        payload.sendNextEmailAt = sendNextEmailAt;
      } else {
        payload.sendNextEmailAt = null;
      }

      await ShopStore.updateNotifications(payload);
      await ShopStore.getShopNotifications(ShopStore.currentShop.id, true);
      AppStore.openModals.set(UNSUBSCRIBE_ALERTS_MODAL_ID, false);
      onSuccess?.();
    } catch (error) {
      if (error.errorFields) {
        notification.error({
          message: 'Error',
          description: 'Please fill in all required fields',
        });
      } else {
        AppStore.addError(
          `Error in updating priority alerts: ${error.message}`
        );
      }
    }
  };

  return (
    <StyledPitstopModal
      id={id}
      title="Edit Notifications"
      width={600}
      okText="Add Alerts"
      closable
      onOk={onOkHandler}
      isManualClose
      cancelButtonProps={{
        type: 'danger',
        ghost: true,
        style: {
          backgroundColor: 'transparent',
          border: 'none',
          color: '#ff4d4f',
          boxShadow: 'none',
        },
      }}
    >
      <StyledP>Weekly Maintenance Schedule</StyledP>

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={12} sm={12}>
            <Form.Item
              name="days"
              label="Run every"
              dependencies={['time']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: async (_, value) => {
                    const time = getFieldValue('time');

                    const isDayEmpty = value === null || value === undefined;
                    const isTimeEmpty = time === null || time === undefined;

                    if (isDayEmpty && isTimeEmpty) return Promise.resolve();
                    if (!isDayEmpty && !isTimeEmpty) return Promise.resolve();

                    return Promise.reject(
                      new Error(
                        'Both "Run every" and "Run At" must be filled or empty together'
                      )
                    );
                  },
                }),
              ]}
            >
              <Select
                style={{ width: '100%' }}
                allowClear
                onChange={() => form.validateFields()}
                options={WEEK_DAYS.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={12}>
            <TimePickerDropdown
              name="time"
              label="Run At"
              onChange={() => form.validateFields()}
              allowClear
              dependencies={['days']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: async (_, value) => {
                    const day = getFieldValue('days');

                    const isDayEmpty = day === null || day === undefined;
                    const isTimeEmpty = value === null || value === undefined;

                    if (isDayEmpty && isTimeEmpty) return Promise.resolve();
                    if (!isDayEmpty && !isTimeEmpty) return Promise.resolve();

                    return Promise.reject(
                      new Error(
                        'Both "Run every" and "Run At" must be filled or empty together'
                      )
                    );
                  },
                }),
              ]}
            />
          </Col>
        </Row>

        <StyledP>Issue Alerts</StyledP>

        <FormMultiSelect
          component={<SelectPriorities />}
          name="priorities"
          label="Priority"
        />
      </Form>
    </StyledPitstopModal>
  );
});

export default UnsibscribeAlertsModal;

function getNextMailingDate(
  dayOfWeek,
  timeStr,
  timezone,
  fromDate = new Date()
) {
  const jsDayOfWeek = dayOfWeek % 7;

  const match = timeStr.match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
  if (!match) throw new Error('Invalid time format');
  let [, hour, minute, ampm] = match;
  hour = parseInt(hour, 10);
  minute = parseInt(minute, 10);
  if (ampm.toLowerCase() === 'pm' && hour !== 12) hour += 12;
  if (ampm.toLowerCase() === 'am' && hour === 12) hour = 0;

  let m = moment.tz(fromDate, timezone);
  let now = m.clone().day(jsDayOfWeek).hour(hour).minute(minute).second(0);

  if (now.isBefore(m)) {
    now.add(7, 'days');
  }

  return now.toDate();
}
